import { useEffect, useMemo, useRef, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Row from 'react-bootstrap/Row'
import ToggleButton from 'react-bootstrap/ToggleButton'
import ToggleButtonGroup from 'react-bootstrap/ToggleButtonGroup'
import {
    createTransaction,
    suggestExchangeRate,
    updateTransaction,
    type GroupDetail,
    type GroupMember,
    type Receipt,
    type Transaction,
} from '../../../../types'
import { actionApiCall, formSubmit } from '../../api/actionApiCall'
import { AmountField, SelectField, SubmitButton, TextField, useFormState } from '../../components/form'
import { useCapabilities } from '../../hooks/useCapabilities'
import { useCurrencies } from '../../hooks/useCurrencies'
import { useInvalidateReceiptQueue, useReceiptQueue } from '../../hooks/useReceipts'
import { formatDateTime } from '../../utils/format'
import { exponentOf, formatAmount, parseAmount, toAmountInputValue } from '../../utils/money'
import { ReceiptQueueModal } from './ReceiptQueueModal'

type SplitMode = 'EQUAL' | 'EXACT'

/** Název, který se u úhrady dluhu doplní sám. Pozná se podle něj, že ho uživatel nezadal. */
const AUTOMATICKY_NAZEV_UHRADY = 'Úhrada dluhu'

export type TransactionFormModalProps = {
    show: boolean
    group: GroupDetail
    /** Bez pohybu jde o přidání nového. */
    transaction?: Transaction
    /** Předvyplnění pro úhradu navrženého dluhu. */
    prefill?: { type: 'TRANSFER'; fromMemberId: string; toMemberId: string; amount: number }
    /**
     * Uživatel místo ručního zadání vybral fotky účtenek. Útrata z nich vznikne
     * až po rozpoznání a kontrole, takže dialog předá soubory volajícímu
     * (dialog přípravy účtenek) a sám se zavře.
     */
    onReceiptFiles?: (files: File[]) => void
    onClose: () => void
    onSaved: () => void
}

/** Hodnota pro <input type="datetime-local"> v místním čase. */
function toLocalInputValue(date: Date): string {
    const offsetMs = date.getTimezoneOffset() * 60_000
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16)
}

export function TransactionFormModal({
    show,
    group,
    transaction,
    prefill,
    onReceiptFiles,
    onClose,
    onSaved,
}: TransactionFormModalProps) {
    const isEdit = !!transaction
    const { data: currencies } = useCurrencies()

    const activeMembers = useMemo(
        () => group.members.filter((member) => member.isActive || isMemberInTransaction(member, transaction)),
        [group.members, transaction],
    )

    const initialCurrency = transaction?.currencyCode ?? group.baseCurrencyCode
    const initialType = prefill?.type ?? transaction?.type ?? 'EXPENSE'
    // Útrata rozdělená podle položek účtenky se upravuje jako přesné částky.
    const initialSplitMode: SplitMode = transaction?.splitMode === 'EXACT' || transaction?.splitMode === 'ITEMS' ? 'EXACT' : 'EQUAL'

    const form = useFormState({
        initialValues: {
            type: initialType as 'EXPENSE' | 'TRANSFER',
            title: transaction?.title ?? (prefill ? AUTOMATICKY_NAZEV_UHRADY : ''),
            note: transaction?.note ?? '',
            currencyCode: initialCurrency,
            amount: toAmountInputValue(
                transaction?.amountTotal ?? prefill?.amount,
                exponentOf(currencies, initialCurrency),
            ),
            exchangeRate: transaction ? String(transaction.exchangeRate).replace('.', ',') : '',
            occurredAt: toLocalInputValue(transaction ? new Date(transaction.occurredAt) : new Date()),
            payerId: prefill?.fromMemberId ?? transaction?.payers[0]?.memberId ?? activeMembers[0]?.id ?? '',
            splitMode: initialSplitMode as SplitMode,
            // Kdo se na útratě podílí; u nového výdaje jsou to všichni aktivní.
            participantIds: transaction
                ? transaction.shares.map((share) => share.memberId)
                : prefill
                  ? [prefill.toMemberId]
                  : activeMembers.map((member) => member.id),
            exactAmounts: buildExactAmounts(transaction, currencies),
        },
        onSubmit: async (values) => {
            const exponent = exponentOf(currencies, values.currencyCode)
            const amountTotal = parseAmount(values.amount, exponent)

            if (amountTotal === undefined || amountTotal <= 0) {
                return { fieldErrors: { amount: 'Zadejte částku větší než nula.' } }
            }
            const isTransfer = values.type === 'TRANSFER'

            if (values.participantIds.length === 0) {
                return { fieldErrors: { shares: 'Vyberte alespoň jednu osobu.' } }
            }

            const shares = isTransfer
                ? [{ memberId: values.participantIds[0]!, amount: amountTotal }]
                : values.participantIds.map((memberId) => ({
                      memberId,
                      amount: values.splitMode === 'EXACT' ? (parseAmount(values.exactAmounts[memberId] ?? '', exponent) ?? 0) : undefined,
                  }))

            const rate = parseRate(values.exchangeRate)
            const body = {
                type: values.type,
                title: values.title,
                note: values.note || null,
                currencyCode: values.currencyCode,
                amountTotal,
                exchangeRate: values.currencyCode === group.baseCurrencyCode ? null : rate,
                occurredAt: new Date(values.occurredAt).toISOString(),
                splitMode: isTransfer ? 'EXACT' : values.splitMode,
                payers: [{ memberId: values.payerId }],
                shares,
                // U úpravy jde o účtenky dodatečně vybrané z fronty.
                receiptIds: isEdit ? pripojovane.map((receipt) => receipt.id) : undefined,
            }

            const { data, fieldErrors } = isEdit
                ? await formSubmit(
                      () => updateTransaction({ path: { transactionId: transaction.id }, body: { ...body, version: transaction.version } }),
                      { successMessage: 'Pohyb byl upraven.' },
                  )
                : await formSubmit(() => createTransaction({ path: { groupId: group.id }, body }), { successMessage: 'Pohyb byl přidán.' })

            if (!data) {
                return { fieldErrors }
            }

            if (pripojovane.length > 0) {
                // Připojené účtenky už do fronty nepatří.
                void invalidateFrontu(group.id)
            }
            onSaved()
            onClose()
        },
    })

    const { setValue } = form

    // ─── Účtenky ──────────────────────────────────────────────────────────────
    // Nová útrata z účtenky vzniká jinde (nahrání → rozpoznání → kontrola);
    // dialog jen nabídne, že fotky předá. U úpravy jde k pohybu připojit
    // účtenku, která čeká ve frontě ke kontrole.
    const { data: capabilities } = useCapabilities()
    const souborInput = useRef<HTMLInputElement>(null)
    const invalidateFrontu = useInvalidateReceiptQueue()

    const [showFronta, setShowFronta] = useState(false)
    const [pripojovane, setPripojovane] = useState<Receipt[]>([])
    const { data: fronta } = useReceiptQueue(group.id, isEdit)
    const pripojovaneIds = pripojovane.map((receipt) => receipt.id)
    const cekaVeFronte = (fronta ?? []).filter((receipt) => !pripojovaneIds.includes(receipt.id)).length

    const prevezmiZFronty = (vybrane: Receipt[]) => {
        setShowFronta(false)
        setPripojovane((predchozi) => [...predchozi, ...vybrane])
    }

    const nabidnoutUctenky = !isEdit && !!onReceiptFiles && (capabilities?.receiptAnalysis || capabilities?.receiptMcp)

    const exponent = exponentOf(currencies, form.values.currencyCode)
    const currencySymbol = currencies?.find((currency) => currency.code === form.values.currencyCode)?.symbol
    const isForeignCurrency = form.values.currencyCode !== group.baseCurrencyCode
    const isTransfer = form.values.type === 'TRANSFER'

    // Kurz se předvyplní podle ČNB; uživatel ho může kdykoli přepsat.
    const [rateLoading, setRateLoading] = useState(false)
    const { currencyCode, occurredAt } = form.values

    useEffect(() => {
        if (!isForeignCurrency || isEdit) {
            return
        }

        let cancelled = false
        setRateLoading(true)

        void actionApiCall(
            () =>
                suggestExchangeRate({
                    path: { groupId: group.id },
                    query: { currencyCode, date: occurredAt.slice(0, 10) },
                }),
            { silent: true },
        ).then((suggestion) => {
            if (!cancelled) {
                setRateLoading(false)
                if (suggestion) {
                    setValue('exchangeRate', String(suggestion.rate).replace('.', ','))
                }
            }
        })

        return () => {
            cancelled = true
        }
    }, [currencyCode, occurredAt, group.id, isForeignCurrency, isEdit, setValue])

    const amountTotal = parseAmount(form.values.amount, exponent) ?? 0
    const exactSum = form.values.participantIds.reduce(
        (sum, memberId) => sum + (parseAmount(form.values.exactAmounts[memberId] ?? '', exponent) ?? 0),
        0,
    )
    const exactDifference = exactSum - amountTotal

    // Přepnutí na úhradu dluhu zúží pohyb na jednoho příjemce a doplní název.
    // Původní stav útraty se schová, aby se po přepnutí zpět nic neztratilo -
    // jinak by útrata zůstala rozdělená jen mezi jednoho člověka a jmenovala by
    // se „Úhrada dluhu", přestože je na záložce Útrata.
    const [zalohaUtraty, setZalohaUtraty] = useState<{ title: string; participantIds: string[] } | undefined>()

    const zmenTyp = (novyTyp: 'EXPENSE' | 'TRANSFER') => {
        if (novyTyp === form.values.type) {
            return
        }
        form.setValue('type', novyTyp)

        if (novyTyp === 'TRANSFER') {
            setZalohaUtraty({ title: form.values.title, participantIds: form.values.participantIds })
            // Úhrada jde vždy někomu jinému než plátci - jinak by nabídka „Komu"
            // ukazovala jiného člověka, než jaký by se odeslal na server.
            const prijemce =
                form.values.participantIds.find((memberId) => memberId !== form.values.payerId) ??
                activeMembers.find((member) => member.id !== form.values.payerId)?.id
            form.setValue('participantIds', prijemce ? [prijemce] : [])
            if (!form.values.title) {
                form.setValue('title', AUTOMATICKY_NAZEV_UHRADY)
            }
            return
        }

        // Zpět na útratu: obnoví se předchozí rozdělení, a když se dialog otevřel
        // rovnou jako úhrada, rozdělí se útrata mezi všechny členy.
        form.setValue('participantIds', zalohaUtraty?.participantIds ?? activeMembers.map((member) => member.id))
        // Zadaný název zůstává; zahazuje se jen ten, který se doplnil sám.
        if (form.values.title === AUTOMATICKY_NAZEV_UHRADY) {
            form.setValue('title', zalohaUtraty?.title ?? '')
        }
        setZalohaUtraty(undefined)
    }

    const toggleParticipant = (memberId: string, checked: boolean) => {
        const next = checked
            ? [...form.values.participantIds, memberId]
            : form.values.participantIds.filter((id) => id !== memberId)
        form.setValue('participantIds', next)
    }

    return (
        <Modal show={show} onHide={onClose} centered scrollable size="lg">
            <form onSubmit={form.handleSubmit} noValidate>
                <Modal.Header closeButton>
                    <Modal.Title as="h2" className="h5">{isEdit ? 'Úprava pohybu' : isTransfer ? 'Úhrada dluhu' : 'Nový pohyb'}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <ToggleButtonGroup
                        type="radio"
                        name="typ-pohybu"
                        className="w-100 mb-3"
                        value={form.values.type}
                        onChange={zmenTyp}
                    >
                        <ToggleButton id="typ-vydaj" value="EXPENSE" variant="outline-primary" className="su-tappable">
                            <i className="bi bi-cart me-1" aria-hidden="true" />
                            Útrata
                        </ToggleButton>
                        <ToggleButton id="typ-uhrada" value="TRANSFER" variant="outline-primary" className="su-tappable">
                            <i className="bi bi-arrow-left-right me-1" aria-hidden="true" />
                            Úhrada dluhu
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {nabidnoutUctenky && !isTransfer && (
                        <div className="border rounded p-2 mb-3 d-grid gap-2">
                            <input
                                ref={souborInput}
                                type="file"
                                accept="image/*,application/pdf"
                                multiple
                                hidden
                                onChange={(event) => {
                                    const files = [...(event.target.files ?? [])]
                                    event.target.value = ''
                                    if (files.length > 0) {
                                        onReceiptFiles(files)
                                    }
                                }}
                            />
                            <Button variant="outline-secondary" className="su-tappable" onClick={() => souborInput.current?.click()}>
                                <i className="bi bi-camera me-1" aria-hidden="true" />
                                Nahrát účtenky
                            </Button>
                            <div className="small text-secondary">
                                Fotky nebo PDF účtenek se přečtou na pozadí. Najdete je pak v sekci Ke kontrole, kde rozeberete
                                položky a útratu potvrdíte.
                            </div>
                        </div>
                    )}

                    {isEdit && (cekaVeFronte > 0 || pripojovane.length > 0) && (
                        <div className="border rounded p-2 mb-3">
                            {pripojovane.map((receipt) => (
                                <div key={receipt.id} className="d-flex align-items-center gap-2 mb-2">
                                    <img
                                        src={`/api/receipts/${receipt.id}/file?variant=thumb`}
                                        alt=""
                                        className="rounded border flex-shrink-0"
                                        style={{ width: '2.5rem', height: '2.5rem', objectFit: 'cover' }}
                                    />
                                    <div className="flex-grow-1 min-w-0 small">
                                        <div className="text-truncate">{receipt.detectedTitle ?? 'Účtenka'}</div>
                                        <div className="text-secondary">Nahráno {formatDateTime(receipt.createdAt)}</div>
                                    </div>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="text-danger p-0 flex-shrink-0"
                                        aria-label="Nepřipojovat tuto účtenku"
                                        onClick={() => setPripojovane((predchozi) => predchozi.filter((polozka) => polozka.id !== receipt.id))}
                                    >
                                        <i className="bi bi-x-lg" aria-hidden="true" />
                                    </Button>
                                </div>
                            ))}

                            {pripojovane.length > 0 && (
                                <div className="small text-secondary mb-2">
                                    {pripojovane.length === 1 ? 'Účtenka se připojí' : 'Účtenky se připojí'} uložením pohybu.
                                </div>
                            )}

                            {cekaVeFronte > 0 && (
                                <div className="d-grid">
                                    <Button
                                        variant="outline-secondary"
                                        className="su-tappable"
                                        data-testid="otevrit-frontu"
                                        onClick={() => setShowFronta(true)}
                                    >
                                        <i className="bi bi-inbox me-1" aria-hidden="true" />
                                        Připojit účtenku z fronty ({cekaVeFronte})
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    <TextField
                        label="Název"
                        value={form.values.title}
                        onChange={(value) => form.setValue('title', value)}
                        error={form.fieldErrors.title}
                        placeholder={isTransfer ? AUTOMATICKY_NAZEV_UHRADY : 'Např. Oběd v restauraci'}
                        required
                    />

                    <Row>
                        <Col xs={12} sm={group.currencyCodes.length > 1 ? 7 : 12}>
                            <AmountField
                                label="Částka"
                                value={form.values.amount}
                                onChange={(value) => form.setValue('amount', value)}
                                currencySymbol={currencySymbol}
                                error={form.fieldErrors.amount ?? form.fieldErrors.amountTotal}
                                required
                            />
                        </Col>
                        {group.currencyCodes.length > 1 && (
                            <Col xs={12} sm={5}>
                                <SelectField
                                    label="Měna"
                                    value={form.values.currencyCode}
                                    onChange={(value) => form.setValue('currencyCode', value)}
                                    error={form.fieldErrors.currencyCode}
                                    options={group.currencyCodes.map((code) => ({ value: code, label: code }))}
                                />
                            </Col>
                        )}
                    </Row>

                    {isForeignCurrency && (
                        <AmountField
                            label={`Kurz (1 ${form.values.currencyCode} = ? ${group.baseCurrencyCode})`}
                            value={form.values.exchangeRate}
                            onChange={(value) => form.setValue('exchangeRate', value)}
                            error={form.fieldErrors.exchangeRate}
                            helpText={
                                rateLoading
                                    ? 'Načítám kurz ČNB…'
                                    : 'Předvyplněno kurzem ČNB k datu pohybu. Pokud jste platili kartou jiným kurzem, přepište ho.'
                            }
                        />
                    )}

                    <TextField
                        label="Datum a čas"
                        type="datetime-local"
                        value={form.values.occurredAt}
                        onChange={(value) => form.setValue('occurredAt', value)}
                        error={form.fieldErrors.occurredAt}
                        required
                    />

                    <SelectField
                        label={isTransfer ? 'Kdo platí' : 'Kdo zaplatil'}
                        value={form.values.payerId}
                        onChange={(value) => form.setValue('payerId', value)}
                        error={form.fieldErrors.payers ?? form.fieldErrors.payerId}
                        options={activeMembers.map((member) => ({ value: member.id, label: member.displayName }))}
                    />

                    {isTransfer ? (
                        <SelectField
                            label="Komu"
                            value={form.values.participantIds[0] ?? ''}
                            onChange={(value) => form.setValue('participantIds', [value])}
                            error={form.fieldErrors.shares}
                            options={activeMembers
                                .filter((member) => member.id !== form.values.payerId)
                                .map((member) => ({ value: member.id, label: member.displayName }))}
                        />
                    ) : (
                        <>
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                                <Form.Label className="mb-0">Rozdělit mezi</Form.Label>
                                <ToggleButtonGroup
                                    type="radio"
                                    name="zpusob-deleni"
                                    size="sm"
                                    value={form.values.splitMode}
                                    onChange={(value: SplitMode) => form.setValue('splitMode', value)}
                                >
                                    <ToggleButton id="deleni-rovnym" value="EQUAL" variant="outline-secondary">
                                        Rovným dílem
                                    </ToggleButton>
                                    <ToggleButton id="deleni-presne" value="EXACT" variant="outline-secondary">
                                        Přesné částky
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </div>

                            <div className="border rounded mb-2">
                                {activeMembers.map((member) => {
                                    const checked = form.values.participantIds.includes(member.id)
                                    return (
                                        <div key={member.id} className="d-flex align-items-center gap-2 px-2 py-1 border-bottom">
                                            <Form.Check
                                                type="checkbox"
                                                id={`ucastnik-${member.id}`}
                                                checked={checked}
                                                label={member.displayName}
                                                className="flex-grow-1"
                                                onChange={(event) => toggleParticipant(member.id, event.target.checked)}
                                            />
                                            {form.values.splitMode === 'EXACT' && checked && (
                                                <Form.Control
                                                    type="text"
                                                    inputMode="decimal"
                                                    size="sm"
                                                    style={{ maxWidth: '8rem' }}
                                                    className="text-end"
                                                    aria-label={`Podíl osoby ${member.displayName}`}
                                                    value={form.values.exactAmounts[member.id] ?? ''}
                                                    onChange={(event) =>
                                                        form.setValue('exactAmounts', {
                                                            ...form.values.exactAmounts,
                                                            [member.id]: event.target.value,
                                                        })
                                                    }
                                                />
                                            )}
                                            {form.values.splitMode === 'EQUAL' && checked && amountTotal > 0 && (
                                                <span className="small text-secondary">
                                                    {formatAmount(
                                                        Math.floor(amountTotal / form.values.participantIds.length),
                                                        exponent,
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>

                            {form.fieldErrors.shares && <div className="invalid-feedback d-block mb-2">{form.fieldErrors.shares}</div>}

                            {form.values.splitMode === 'EXACT' && amountTotal > 0 && (
                                <Alert variant={exactDifference === 0 ? 'success' : 'warning'} className="py-2 small">
                                    {exactDifference === 0
                                        ? 'Součet podílů odpovídá celkové částce.'
                                        : `Součet podílů je ${formatAmount(exactSum, exponent)}, celková částka ${formatAmount(amountTotal, exponent)} (rozdíl ${formatAmount(exactDifference, exponent)}).`}
                                </Alert>
                            )}
                        </>
                    )}

                    <TextField
                        label="Poznámka"
                        value={form.values.note}
                        onChange={(value) => form.setValue('note', value)}
                        error={form.fieldErrors.note}
                    />
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={onClose} className="su-tappable">
                        Zrušit
                    </Button>
                    <SubmitButton submitting={form.submitting}>{isEdit ? 'Uložit' : 'Přidat'}</SubmitButton>
                </Modal.Footer>
            </form>

            {showFronta && (
                <ReceiptQueueModal
                    show={showFronta}
                    group={group}
                    skrytaIds={pripojovaneIds}
                    potvrzeni="Připojit k pohybu"
                    onConfirm={prevezmiZFronty}
                    onClose={() => setShowFronta(false)}
                />
            )}
        </Modal>
    )
}

function isMemberInTransaction(member: GroupMember, transaction: Transaction | undefined): boolean {
    if (!transaction) {
        return false
    }
    return (
        transaction.shares.some((share) => share.memberId === member.id) ||
        transaction.payers.some((payer) => payer.memberId === member.id)
    )
}

function buildExactAmounts(transaction: Transaction | undefined, currencies: ReturnType<typeof useCurrencies>['data']): Record<string, string> {
    if (!transaction) {
        return {}
    }

    const exponent = exponentOf(currencies, transaction.currencyCode)
    return Object.fromEntries(transaction.shares.map((share) => [share.memberId, toAmountInputValue(share.amount, exponent)]))
}

/** Kurz se zadává s desetinnou čárkou; prázdná hodnota znamená „dopočti". */
function parseRate(text: string): number | null {
    const normalized = text.replace(/\s| /g, '').replace(',', '.')
    if (normalized === '') {
        return null
    }
    const value = Number(normalized)
    return Number.isFinite(value) && value > 0 ? value : null
}
